/*
  shintop-more — caps a long panel at a readable preview and offers "See more".

  WHY. Naoya, 2026-09-06, on the product page's About tab: "not everything will be displayed,
  give users the option to click see more… if they wanna read more of the about." A full About
  body runs several hundred words — the pronunciation, the sauce, the maker's own line, then a
  "What you're getting" list — and it pushes the Ingredients and Allergens tabs, and everything
  below the tabs, a screen and a half down the page. The panel is a reference, not a pitch: a
  shopper reads the opening, and the ones who want the rest ask for it.

  WHAT IT DOES. Measures its content against a cap, and only if the content is meaningfully
  taller adds a clip class and reveals the toggle. Clicking swaps the label and the clip.

  FOUR THINGS THAT MAKE IT SAFE

  1. IT DEGRADES TO NOTHING. The clip is a CLASS THIS FILE ADDS, never a stylesheet default, and
     the button ships `hidden`. With JavaScript off — or if this file 404s — the panel is the
     full, open disclosure it has always been, which is the guarantee snippets/shintop-tab-panel
     .liquid was built around ("no content is ever unreachable"). A CSS-default clip with a
     JS-only opener would put the whole About behind a script; that trade was available and is
     refused.

  2. IT MEASURES WHEN THERE IS SOMETHING TO MEASURE. A tab that is not the open one carries
     `[hidden]`, and a closed accordion `<details>` hides its own body, so `scrollHeight`
     inside either is 0 — measuring once at upgrade would decide "this fits" for every panel
     except About and the button would never appear on any of the others. A ResizeObserver on
     the body sees the box go from nothing to real the moment a tab is selected or an accordion
     is opened, which is also exactly when the width changes on rotate or resize. Re-measuring
     on every observation rather than caching means a reflow that makes text taller can clip
     content that previously fitted.

  3. IT DOES NOT RE-CLIP WHAT A READER OPENED. Once `expanded` is set, the observer keeps
     updating the measurement but never re-applies the clip. Collapsing a panel under someone
     mid-sentence because they rotated their phone is a worse bug than the one this fixes.

  4. THE SLACK IS THE POINT. Clipping content that overruns the cap by a line and a half hides
     almost nothing and costs a click; the toggle only appears once there is a real amount of
     text behind it. See --shintop-more-slack in the stylesheet.

  Strings come from the locale through data attributes — never from this file.
*/

class ShintopMore extends HTMLElement {
  connectedCallback() {
    this.content = this.querySelector('[data-shintop-more-content]');
    this.toggle = this.querySelector('[data-shintop-more-toggle]');
    if (!this.content || !this.toggle) return;

    this.expanded = false;
    this.toggle.addEventListener('click', () => this.setExpanded(!this.expanded));

    this.measure();

    // Covers all three moments the answer can change: a hidden tab becoming visible, an
    // accordion opening, and a resize reflowing the prose. One observer, because to this
    // element they are the same event — the content box now has a different height.
    this.resizeObserver = new ResizeObserver(() => this.measure());
    this.resizeObserver.observe(this.content);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  /*
    The cap and the slack are CSS, not constants here: they are typography decisions, they
    belong on the scale, and a media query can change them without touching this file.

    THEY ARE MEASURED, NOT READ, AND THE FIRST VERSION READ THEM. `getComputedStyle(el)
    .getPropertyValue('--shintop-more-cap')` on an unregistered custom property returns the
    TOKEN STREAM after variable substitution — the literal string "calc(1.65 * 1.7rem * 12)".
    It is never evaluated to a length, so `parseFloat` returned NaN, the cap fell to 0, and
    measure() bailed on every panel: a 1264px About rendered whole with no button and looked
    exactly like a component that had decided the content fitted.

    So the browser is asked to do the arithmetic instead. The probe is appended to this element
    so it inherits the properties, `height` is a real length property, and the style engine
    resolves the calc and the rem together — including a rem that changed since page load,
    which a cached number would miss. It is absolutely positioned and hidden, so it adds
    nothing to the flow and cannot perturb the box the ResizeObserver is watching.

    THE VALUE IS READ FROM getComputedStyle, NOT FROM getBoundingClientRect, and the difference
    is not academic: measured in Chrome inside this panel, the rect came back 0 while the
    computed `height` on the same element in the same frame read "336.6px" — the probe resolves
    its length but does not get a laid-out box here. Only the resolved length is wanted, so the
    rect was the wrong question; going through layout also risks answering 0 in any subtree
    that is not being laid out, which is precisely where a hidden tab lives.
  */
  limits() {
    const probe = document.createElement('div');
    probe.style.cssText =
      'position:absolute;top:0;left:0;visibility:hidden;pointer-events:none;width:0;';
    this.appendChild(probe);
    const read = (prop) => {
      probe.style.height = `var(${prop})`;
      const px = parseFloat(getComputedStyle(probe).height);
      return Number.isFinite(px) ? px : 0;
    };
    const cap = read('--shintop-more-cap');
    const slack = read('--shintop-more-slack');
    probe.remove();
    return { cap, slack };
  }

  measure() {
    const { cap, slack } = this.limits();
    // A cap of 0 means either that the custom properties did not resolve (sheet missing, or
    // mounted outside `.shintop`) or that a media query switched the cap OFF — below 750px it
    // is set to 0px deliberately. Either way the answer is the same: behave as if the content
    // fits, which is the uncapped panel.
    //
    // IT MUST UNDO, NOT MERELY DECLINE (2026-09-06). Returning early only did nothing on a
    // FIRST measure. Resizing a clipped desktop panel down past 750px re-ran measure() with
    // cap 0 and left the clip class, the inline --shintop-more-clip and the revealed button
    // exactly where they were — so the phone layout kept the preview it was supposed to lose.
    if (cap <= 0) {
      this.classList.remove('shintop-more--clipped', 'shintop-more--cut');
      this.style.removeProperty('--shintop-more-clip');
      this.toggle.hidden = true;
      return;
    }

    // scrollHeight is the unclipped height even while the clip class is applied, because the
    // clip is max-height + overflow on this same box. That invariance is what stops the
    // observer oscillating: applying the clip changes the box, so ResizeObserver fires once
    // more, but the number this decision is made from did not move — so the second pass
    // reaches the same verdict, changes no class, and the cycle ends.
    const full = this.content.scrollHeight;
    if (full === 0) return; // hidden tab or closed accordion: nothing to measure yet

    const overflows = full > cap + slack;
    this.toggle.hidden = !overflows;

    if (!overflows) {
      this.classList.remove('shintop-more--clipped', 'shintop-more--cut');
      this.style.removeProperty('--shintop-more-clip');
      return;
    }

    const { height, snapped } = this.clipHeight(cap);
    this.style.setProperty('--shintop-more-clip', `${height}px`);
    this.classList.toggle('shintop-more--cut', !snapped);
    // Rule 3: an open panel stays open.
    if (!this.expanded) this.classList.add('shintop-more--clipped');
  }

  /*
    WHERE THE PREVIEW ENDS. The first version cut at the cap exactly and let a gradient mask
    soften the edge, and the render said no: on the Bisco page the cap landed 7px past the
    "What you're getting" subhead, so the preview ended on a HALF-FADED HEADING. That does not
    read as "there is more below" — it reads as a component that failed to paint, which is the
    one impression a truncation control cannot afford.

    So the cut snaps back to the last CHILD BLOCK that fits whole. A preview then always ends
    on a finished paragraph or list, with no half-line and nothing ghosted.

    AND NEVER ON A HEADING, even a whole one. A subhead is a promise about what follows it;
    showing "What you're getting" with nothing underneath is a worse tease than not showing it,
    and it makes the panel look like it lost its content rather than folded it. When the last
    fitting block is a heading the cut steps back past it — repeatedly, since two stacked
    headings are possible — and the heading goes behind the button with the section it belongs
    to.

    The fallback matters as much as the rule: a first block TALLER than the cap (one long
    unbroken paragraph) snaps to nothing, and there the pixel cap and the fade are right — a
    mid-paragraph cut is legible as a cut, which is exactly what a mid-heading cut is not.
    That is the only case that gets `--cut`, and so the only case that gets a mask.

    Child rects, not offsetTop: offsetTop is measured from the nearest positioned ancestor,
    which is not necessarily this box. Rects stay correct while the clip is applied, because
    `overflow: hidden` hides the overflow without moving anything.
  */
  clipHeight(cap) {
    const base = this.content.getBoundingClientRect().top;
    let height = 0;
    let last = null;
    for (const child of this.content.children) {
      const bottom = child.getBoundingClientRect().bottom - base;
      if (bottom > cap) break;
      height = bottom;
      last = child;
    }
    while (last && /^H[1-6]$/.test(last.tagName)) {
      const prev = last.previousElementSibling;
      height = prev ? prev.getBoundingClientRect().bottom - base : 0;
      last = prev;
    }
    return height > 0 ? { height, snapped: true } : { height: cap, snapped: false };
  }

  /*
    COLLAPSING MUST BRING THE TOP OF WHAT YOU COLLAPSED BACK INTO VIEW (2026-09-06). Naoya:
    "after clicking see less of about, it would not go up to the one in second image, and just
    up to the first image."

    Measured before building. The panel is collapsed while the reader is at the BOTTOM of it, so
    several hundred pixels vanish from above the viewport. Chrome's scroll anchoring does fire —
    scrollY went 2023 -> 1462 on its own, matching the 561px removed — but anchoring only keeps
    a nearby element steady; it makes no promise about framing the thing that changed. Measured
    after the collapse: the tab strip sat at **-84** and the panel top at **-3**. Both just above
    the fold, which is why "See less" appeared to do nothing but move the page a little.

    So: only when collapsing, and only when the top is actually out of view, scroll it back.

    THE ANCHOR IS DECLARED, NOT GUESSED. `shintop-more` wraps prose and knows nothing about what
    sits above it; for the tabs that is the STRIP, which is a sibling two levels up. A component
    marks itself with `data-shintop-more-anchor` and gets scrolled instead of the wrapper —
    without it this file would have had to name `shintop-tabs`, which is the wrong direction of
    dependency for a shared component.

    THE HEADER OFFSET IS MEASURED, NOT A CONSTANT. `<sticky-header>` is `position: relative` at
    rest and goes fixed while the page scrolls UP — which is the only direction this ever
    scrolls — so landing the anchor at viewport top would tuck it under 106px of header.
  */
  revealTop() {
    const anchor = this.closest('[data-shintop-more-anchor]') || this;
    requestAnimationFrame(() => {
      const top = anchor.getBoundingClientRect().top;
      const header = document.querySelector('sticky-header');
      const offset = header ? header.getBoundingClientRect().height : 0;
      // Already fully visible: do not move the page under a reader who did not ask for it.
      if (top >= offset) return;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: window.scrollY + top - offset, behavior: reduce ? 'auto' : 'smooth' });
    });
  }

  setExpanded(next) {
    this.expanded = next;
    this.classList.toggle('shintop-more--clipped', !next);
    this.toggle.setAttribute('aria-expanded', String(next));
    if (!next) this.revealTop();

    const label = next ? this.dataset.labelLess : this.dataset.labelMore;
    const aria = next ? this.dataset.ariaLess : this.dataset.ariaMore;
    const text = this.toggle.querySelector('[data-shintop-more-label]');
    if (text && label) text.textContent = label;
    if (aria) this.toggle.setAttribute('aria-label', aria);

    /*
      S08-03 (audit 2026-09-13, chunk 9). A SECOND, NAIVE SCROLL CORRECTION USED TO SIT HERE,
      ten lines below the 36-line comment above `revealTop()` explaining why that shape is
      wrong. It read:

        if (!next) {
          const top = this.getBoundingClientRect().top;
          if (top < 0) this.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }

      Two scroll corrections fired on every collapse. The outcome was correct only because
      `revealTop()` ran second and won — it scrolls the DECLARED anchor rather than this
      wrapper, and it subtracts the measured sticky-header height, so the anchor landed at the
      106 px the round measured. The superseded version scrolled the wrapper to viewport top,
      under the header.

      Deleted rather than left in place because the risk was never the pixels, it was the next
      person: two call sites for one behaviour means a future change lands in one of them, and
      the one that looks simpler is the wrong one. `revealTop()` is called from the line above
      and is the only correction.
    */
  }
}

if (!customElements.get('shintop-more')) {
  customElements.define('shintop-more', ShintopMore);
}
